import { observer } from 'mobx-react-lite';
import { type ChangeEvent, type DragEvent, useRef } from 'react';
import {
  CheckCircle,
  FileCsv,
  GearSix,
  UploadSimple,
  WarningCircle,
  XCircle,
} from '@phosphor-icons/react';
import { type SourceKind } from '@/domain/beforered';
import { useT } from '../useT';
import { SlotHelpButton } from './SlotHelpButton';
import { type ImportSlotStore } from './ImportSlotStore';

const REQUIRED: Record<SourceKind, boolean> = {
  BOM: true,
  INVENTORY: false,
  SALES: false,
  MFG: false,
  PURCHASE: false,
  PARTS: false,
};

interface Props {
  store: ImportSlotStore;
}

export const ImportSlotView = observer(function ImportSlotView({ store }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const t = useT();

  const onDrop = (e: DragEvent): void => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) void store.drop(file);
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) void store.drop(file);
    if (inputRef.current) inputRef.current.value = '';
  };

  const onPick = (): void => inputRef.current?.click();
  const onDragOver = (e: DragEvent): void => e.preventDefault();

  const required = REQUIRED[store.source];
  const valid = store.state === 'valid';
  const mapping = store.state === 'mapping';
  const parsing = store.state === 'parsing';
  const errored = store.state === 'error';

  const labels: Record<SourceKind, string> = {
    BOM: t.slots.bom,
    INVENTORY: t.slots.inventory,
    SALES: t.slots.sales,
    MFG: t.slots.mfg,
    PURCHASE: t.slots.purchase,
    PARTS: t.slots.parts,
  };

  return (
    <div
      onDrop={onDrop}
      onDragOver={onDragOver}
      onClick={onPick}
      className={`group relative flex cursor-pointer flex-col gap-2 border-2 bg-white p-4 text-sm transition dark:bg-steel-900 ${
        valid
          ? 'border-light-green'
          : errored
            ? 'border-light-red'
            : mapping
              ? 'border-amber-signal'
              : 'border-steel-300 hover:border-amber-signal dark:border-steel-700'
      }`}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={onChange}
        className="hidden"
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {valid ? (
            <CheckCircle weight="fill" className="text-light-green" size={18} />
          ) : errored ? (
            <XCircle weight="fill" className="text-light-red" size={18} />
          ) : mapping ? (
            <GearSix weight="fill" className="animate-spin text-amber-signal" size={18} />
          ) : parsing ? (
            <GearSix weight="fill" className="animate-spin text-steel-500" size={18} />
          ) : (
            <FileCsv weight="duotone" className="text-steel-400" size={18} />
          )}
          <span className="font-mono text-xs font-bold tracking-wider text-steel-700 dark:text-steel-200">
            {labels[store.source]}
            {required && <span className="ml-1 text-amber-signal">*</span>}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {valid && (
            <span className="font-mono text-xs text-light-green-deep dark:text-light-green">
              {store.rowCount} {t.slots.rows}
            </span>
          )}
          {parsing && <span className="font-mono text-xs text-steel-500">{t.slots.parsing}</span>}
          <SlotHelpButton source={store.source} />
        </div>
      </div>

      {store.state === 'empty' && (
        <div className="flex items-center gap-1 font-mono text-xs text-steel-500">
          <UploadSimple size={14} />
          <span>{t.slots.dropHint}</span>
        </div>
      )}
      {valid && (
        <span className="truncate font-mono text-xs text-steel-500">{store.filename}</span>
      )}
      {mapping && (
        <span className="flex items-center gap-1 font-mono text-xs text-amber-deep">
          <WarningCircle weight="fill" size={14} />
          {t.slots.mapColumns}
        </span>
      )}
      {errored && (
        <span className="font-mono text-xs text-light-red-deep dark:text-light-red">
          {t.slots.failed}: {store.errorMessage}
        </span>
      )}
    </div>
  );
});
